import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';

import { checkboxUIComponent } from 'ctm-css-themes';
import { sanitizeInnerHtml, interpolateString, applyVerticalStyles, get } from 'ctm-util-functions';

import { getCheckboxDisabledProp, getRadioComponentStateSuffix } from '../utils';
import * as actions from '../actions';
import { HelpIndicator } from '../help_indicator/help_indicator';
import { SimpleFlexbox } from '../../flexbox/flexbox';

import styles from './checkbox.css';

class Checkbox extends Component {

    /**
     * Default the focused state to not focused.
     * @param {Object} props The component properties
     */
    constructor(props) {
        super(props);
        this.state = {
            focus: false
        };
        this.styles = applyVerticalStyles(styles, 'checkbox');
    }

    /**
     * If the input should be checked by default (checked: true in schema), set the value for the state during
     * onMount and the input will appear checked
     */
    componentDidMount = () => {
        const { nonJourneyField, schema, dispatch, touched, value } = this.props;

        if (nonJourneyField) {
            // this prevents multiple, unneeded actions from being dispatched when a checkbox is used in a CheckboxListFilter
            return;
        }

        // Defaulting value to 'N' if unChecked, then update the store
        const elementProps = schema.field;
        let currentValue = 'N';
        if (elementProps.checked || value === elementProps.value) {
            currentValue = elementProps.value;
        } else if (elementProps.default !== undefined) {
            currentValue = elementProps.default;
        }

        // If unChecked, updated the store to 'N' when mount, only do this for non-grouped checkboxes (key, value pair in store)
        // @example: { optinPrivacy: 'N' }
        if (currentValue !== value && !schema.isGrouped) {
            dispatch(
                actions.setFormValue(
                    schema.backendIdentifier || schema.identifier,
                    currentValue,
                    schema.stateKey,
                    touched || false
                )
            );
        }
    };

    /**
     * On updates, check if this input is disabled, if it is, dispatch an action to ensure that it's value is not
     * set on state for the input's identifier.
     */
    componentDidUpdate = () => {
        const { dispatch, schema, value } = this.props;
        // In some case we want to reset the value to null when a checkbox is disabled, but we don't want this functionality
        // for Energy T&C checkboxes
        const dontReset = get(this.props, 'schema.field.dontResetWhenDisabled', false);
        if (getCheckboxDisabledProp(this.props).disabled === true && !dontReset) {
            dispatch(
                actions.resetValueIfSet(
                    schema.identifier,
                    value,
                    schema.stateKey
                )
            );
        }
    };

    /**
     * Call the setValue function when the value of the input has been changed
     * by the user.
     * @param {Object} event - event object
     */
    onChange = (event) => {
        event.persist();

        const { schema, nonJourneyField, dispatch } = this.props;
        // if checked, set the value, else, clear the value
        let value = 'N';
        let isChecked = 'unchecked';
        if (event.target.checked) {
            value = event.target.value;
            isChecked = 'checked';
        }

        // If nonJourneyField, i.e. Results filter bar, don't update form state
        if (!nonJourneyField) {
            // isGrouped means the values in store needs to be an array, so we call a different action to update the list
            if (schema.isGrouped) {
                dispatch(
                    actions.setValueInArray(
                        schema.backendIdentifier || schema.identifier,
                        event.target.value,
                        schema.stateKey,
                        true
                    )
                );
            } else {
                // Fallback to default action, update store with key, value pair
                dispatch(
                    actions.setFormValue(
                        schema.backendIdentifier || schema.identifier,
                        value,
                        schema.stateKey,
                        true
                    )
                );
            }
        }

        // If we need to call actions directly or action creators, or trigger an onChangeFunction
        // If you pass an action that is defined as a form action, it will dispatch an action creator,
        // otherwise it will just dispatch the action
        const eventProps = schema.eventProps;

        if (eventProps) {
            // Run a callback
            if (eventProps.onChangeFunction) {
                eventProps.onChangeFunction(event);
            }
            // Dispatch an action or action creator
            if (eventProps.onChange && eventProps.onChange[isChecked]) {
                const eventActions = eventProps.onChange[isChecked].actions;
                for (const action of eventActions) {
                    if (actions[action.type]) {
                        // Form specific actions
                        dispatch(actions[action.type](action, schema.stateKey));
                    } else {
                        dispatch(action);
                    }
                }
            }
        }
    };

    /**
     * We need to apply focused states to container divs, can't use :focus pseudo selector,
     * so we need to re-render the component.
     * @param {Boolean} state True if focusing, False is blurring.
     */
    toggleFocus = (state) => {
        this.setState({
            focus: state
        });
    };

    /**
     * Get the label for the checkbox.
     * @param  {Object} elementProps - The content props.
     * @param  {String} elementId - The elements unique ID.
     * @returns {Array} The children components array
     */
    getContent = (elementProps, elementId) => {
        const { controlPositionClassName, selectedValues } = this.props;
        const label = [];
        const textStyle = controlPositionClassName ? this.styles.new_line_text : this.styles.inline_text;
        if (elementProps.noBadgeLabel) {
            label.push(
                <span className={this.styles.inline_no_text} key={`${elementId}.label`} />
            );
        } else if (elementProps.label) {
            label.push(
                <span className={textStyle} key={`${elementId}.label`}>
                    {interpolateString(elementProps.label)(selectedValues)}
                </span>
            );
        } else if (elementProps.innerHtml) {
            const html = interpolateString(sanitizeInnerHtml(elementProps.innerHtml))(selectedValues);
            // eslint-disable-next-line react/no-danger
            label.push(<span dangerouslySetInnerHTML={{ __html: html }}
                className={textStyle}
                key={`${elementId}.innerHtml`} />);
        } else if (elementProps.labelEl) {
            label.push(
                <span className={textStyle} key={`${elementId}.label`}>
                    {elementProps.labelEl}
                </span>
            );
        }
        return label;
    };

    /**
     * Get the tooltip
     * @param  {Object} schema - element schema.
     * @returns {Node|null} - HelpIndicator component
     */
    getHelpText = ({ helpText, identifier }) => {
        if (!helpText) {
            return null;
        }

        const helpTextProps = {
            schema: { identifier: `${identifier}_tooltip` },
            contentProps: { ...helpText }
        };
        return <SimpleFlexbox display="flex"><HelpIndicator {...helpTextProps} /></SimpleFlexbox>;
    };

    /**
     * Render a single checkbox and a label.
     * @returns {Node} A rendered badge component in a container.
     */
    render = () => {
        const {
            controlPositionClassName,
            schema,
            value,
            selectedValues,
            className
        } = this.props;
        const { focus } = this.state;
        const elementProps = schema.field;
        const elementValue = interpolateString(elementProps.value)(selectedValues);
        const elementId = `${schema.identifier}.${elementValue}`;
        const isChecked = value === elementValue;

        // UI Component
        const UIComponentPrefix = checkboxUIComponent[elementProps.UIComponent] ? elementProps.UIComponent : 'checkbox_medium_rounded';
        const stateSuffix = getRadioComponentStateSuffix(this.props);
        const UIComponent = `${UIComponentPrefix}${stateSuffix}`;
        const focusClass = focus ? 'state_set_focused' : null;
        // When the checkbox is floating up near the top of a badge, it needs to be inline-block.
        // Due to the div wrapper around it, the span loses its display block from label's display: flex.
        const checkboxDisplay = controlPositionClassName ? 'inline_block' : 'block';
        const componentClass = classNames(checkboxUIComponent[UIComponent],
            checkboxUIComponent[focusClass],
            styles[`display_${checkboxDisplay}`]);

        // checkbox label styles
        const labelClass = classNames(className,
            this.styles.label,
            this.styles[`variant_${elementProps.variant}`],
            this.styles[`checkbox_label${stateSuffix}`]);
        const checkboxContainerClassName = classNames(controlPositionClassName, styles.checkbox_container);
        return (
            <>
                {this.getHelpText(schema)}
                <label
                    htmlFor={elementId}
                    className={labelClass}
                    onFocus={() => this.toggleFocus(true)}
                    onBlur={() => this.toggleFocus(false)}
                >
                    <div className={checkboxContainerClassName}>
                        <span className={componentClass}>
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={this.onChange}
                                {...getCheckboxDisabledProp(this.props)}
                                name={schema.identifier}
                                value={elementValue}
                                id={elementId}
                                data-id={elementId}
                            />
                        </span>
                    </div>
                    {this.getContent(elementProps, elementId)}
                </label>
            </>
        );
    };

}

Checkbox.propTypes = {
    // Form Schema
    schema: PropTypes.shape({
        eventProps: PropTypes.object,
        field: PropTypes.object.isRequired,
        identifier: PropTypes.string.isRequired,
        backendIdentifier: PropTypes.string,
        stateKey: PropTypes.string,
        isGrouped: PropTypes.bool
    }).isRequired, // The schema for this form element
    className: PropTypes.string,
    value: PropTypes.string.isRequired,
    touched: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    selectedValues: PropTypes.object,
    controlPositionClassName: PropTypes.string,
    nonJourneyField: PropTypes.bool
};

export { Checkbox };
